import React, { MouseEvent, ReactElement, ReactNode } from 'react'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import TableHeadMUI from '@material-ui/core/TableHead'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Checkbox from '@material-ui/core/Checkbox'
import { SELECTION_STATE, SelectionState } from '../../../Hooks/useSelection'
import { SORT_DIRECTION, SortDirection } from '../../../Hooks/useSort'

interface Column<T, K extends keyof T> {
    /**
     * The object property that will be rendered in each cell.
     *
     * For example if we have data like this [{name: "yanis"}]
     * and we want to show the name
     * we need to set the field to "name" in the columns [{field:"name"}].
     *
     * "renderCell" will take precedence over "field".
     */
    field: K
    /**
     * React key, has to be unique.
     */
    key: string
    /**
     * Name that is going to be displayed on the head column.
     */
    headerName?: string
    /**
     * Disables sorting for the column.
     */
    sortable?: boolean
}

export type OnSortColumnClickFn<T, K extends keyof T> = (column: Column<T, K>) => void

interface TableHeadProps<T, K extends keyof T> {
    columns: Column<T, K>[]

    isSelectable: boolean
    selectionState?: SelectionState
    onSelectAllClick: () => void

    onSortColumnClick: OnSortColumnClickFn<T, K>
    sortDirection: SortDirection
    sortOrderBy?: keyof T
}

type CreateSortHanlderFn<T, K extends keyof T> = (column: Column<T, K>) => (event: MouseEvent<unknown>) => void

export function TableHead<T, K extends keyof T>({
    columns,

    isSelectable,
    selectionState,
    onSelectAllClick,

    onSortColumnClick,
    sortDirection,
    sortOrderBy
}: TableHeadProps<T, K>): ReactElement {
    const createSortHandler: CreateSortHanlderFn<T, K> = (column: Column<T, K>) => (): void => {
        onSortColumnClick(column)
    }
    return (
        <TableHeadMUI className='router-datatable-head'>
            <TableRow>
                {isSelectable ? (
                    <TableCell
                        padding='checkbox'
                        key='select-all-toggle'
                        className='router-datatable-head-selection-cell'
                    >
                        <Checkbox
                            indeterminate={selectionState === SELECTION_STATE.PARTIAL}
                            checked={selectionState === SELECTION_STATE.ALL}
                            onClick={(): void => onSelectAllClick()}
                        />
                    </TableCell>
                ) : (
                    <></>
                )}
                {columns.map(
                    (headCell: Column<T, K>): ReactNode => (
                        <TableCell key={headCell.key || headCell.headerName} align='left'>
                            {headCell.sortable ? (
                                <TableSortLabel
                                    active={sortOrderBy === headCell.field && sortDirection !== false}
                                    direction={
                                        sortOrderBy === headCell.field
                                            ? sortDirection || SORT_DIRECTION.ASCENDING
                                            : SORT_DIRECTION.ASCENDING
                                    }
                                    onClick={createSortHandler(headCell)}
                                    IconComponent={ArrowDropDownIcon}
                                    hideSortIcon={false}
                                >
                                    {headCell.headerName}
                                </TableSortLabel>
                            ) : (
                                headCell.headerName
                            )}
                        </TableCell>
                    )
                )}
            </TableRow>
        </TableHeadMUI>
    )
}
